import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Power, LayoutGrid, List } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, DataTable, StatusBadge, fmt } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const EMPTY = { name: "", commercial_name: "", rnc: "", address: "", phones: "", emails: "", ncf_prefix: "B02", plan_id: "", activation_preset: "none", active_until: "", i18n_enabled: false };

function ActivationBadge({ s }) {
  if (!s.active_until) return <span className="text-xs text-slate-400">Sin límite</span>;
  if (s.expired) {
    return (
      <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" data-testid={`school-expired-${s.id}`}>
        Vencido · {s.active_until}
      </span>
    );
  }
  const urgent = (s.days_left ?? 99) <= 7;
  return (
    <span
      className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${urgent ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}
      data-testid={`school-activeuntil-${s.id}`}
    >
      Vence {s.active_until} · {s.days_left}d
    </span>
  );
}

export default function Schools() {
  const [schools, setSchools] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [viewMode, setViewMode] = useState(localStorage.getItem("schoolsView") || "table");

  const switchView = (mode) => {
    setViewMode(mode);
    localStorage.setItem("schoolsView", mode);
  };

  const load = useCallback(() => {
    api.get("/schools").then((r) => setSchools(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const planOf = (s) => plans.find((p) => p.id === s.plan_id);

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, commercial_name: s.commercial_name || "", rnc: s.rnc || "",
      address: s.address || "", phones: (s.phones || []).join(", "),
      emails: (s.emails || []).join(", "), ncf_prefix: s.ncf_prefix || "B02", plan_id: s.plan_id || "",
      activation_preset: s.active_until ? "custom" : "none", active_until: s.active_until || "",
      i18n_enabled: !!s.i18n_enabled,
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const payload = {
        ...form,
        phones: form.phones ? form.phones.split(",").map((s) => s.trim()).filter(Boolean) : [],
        emails: form.emails ? form.emails.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      delete payload.activation_preset;
      if (form.activation_preset === "none") payload.active_until = "";
      else if (form.activation_preset !== "custom") {
        const d = new Date();
        d.setDate(d.getDate() + parseInt(form.activation_preset));
        payload.active_until = d.toISOString().slice(0, 10);
      } else {
        payload.active_until = form.active_until || "";
      }
      if (editing) {
        await api.put(`/schools/${editing.id}`, payload);
        toast.success("Colegio actualizado");
      } else {
        await api.post("/schools", payload);
        toast.success("Colegio creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggle = async (s) => {
    try {
      const r = await api.post(`/schools/${s.id}/toggle`);
      toast.success(r.data.active ? "Colegio activado" : "Colegio desactivado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const columns = [
    { key: "name", label: "Colegio", render: (s) => <span className="font-medium text-slate-900">{s.name}</span> },
    { key: "rnc", label: "RNC", render: (s) => <span className="font-mono text-xs">{s.rnc}</span> },
    { key: "students_count", label: "Estudiantes" },
    { key: "users_count", label: "Usuarios" },
    {
      key: "plan_id", label: "Suscripción",
      render: (s) => {
        const p = planOf(s);
        return p ? (
          <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200" data-testid={`school-plan-${s.id}`}>
            {p.name} · {fmt(p.price_per_student)}/est
          </span>
        ) : <span className="text-xs text-slate-400">Sin plan</span>;
      },
    },
    {
      key: "monthly", label: "Costo est. mensual",
      render: (s) => {
        const p = planOf(s);
        return p ? <span className="font-semibold text-slate-800">{fmt(p.price_per_student * (s.students_count || 0))}</span> : "—";
      },
    },
    { key: "active", label: "Estado", render: (s) => <StatusBadge value={s.active ? "activo" : "retirado"} /> },
    { key: "active_until", label: "Activación", render: (s) => <ActivationBadge s={s} /> },
    { key: "i18n_enabled", label: "Idiomas", render: (s) => s.i18n_enabled ? <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200" data-testid={`school-i18n-${s.id}`}>ES · EN · FR</span> : <span className="text-xs text-slate-400">Solo ES</span> },
    {
      key: "_a", label: "Acciones",
      render: (s) => (
        <div className="flex gap-1">
          <Button size="sm" variant="outline" data-testid={`edit-school-${s.id}`} onClick={() => openEdit(s)}>Editar</Button>
          <Button size="sm" variant="ghost" className={s.active ? "text-rose-600" : "text-emerald-600"} data-testid={`toggle-school-${s.id}`} onClick={() => toggle(s)}>
            <Power className="w-4 h-4 mr-1" /> {s.active ? "Desactivar" : "Activar"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="schools-page">
      <PageHeader title="Colegios" subtitle="Administración central multi-tenant">
        <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="schools-view-toggle">
          <button
            data-testid="view-table-button"
            onClick={() => switchView("table")}
            className={`px-3 py-2 text-sm transition-colors ${viewMode === "table" ? "bg-blue-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            title="Vista de tabla"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            data-testid="view-cards-button"
            onClick={() => switchView("cards")}
            className={`px-3 py-2 text-sm transition-colors ${viewMode === "cards" ? "bg-blue-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            title="Vista de tarjetas"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
        </div>
        <Button data-testid="create-school-button" className="bg-blue-800 hover:bg-blue-700" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }}>
          <Plus className="w-4 h-4 mr-1" /> Nuevo colegio
        </Button>
      </PageHeader>
      {viewMode === "table" ? (
        <DataTable columns={columns} rows={schools} testid="schools-table" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="schools-cards">
          {schools.map((s) => {
            const p = planOf(s);
            return (
              <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`school-card-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-slate-900">{s.name}</p>
                  <StatusBadge value={s.active ? "activo" : "retirado"} />
                </div>
                <p className="text-xs font-mono text-slate-500 mt-1">RNC: {s.rnc || "—"}</p>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{s.address}</p>
                <div className="flex gap-4 mt-3 text-sm">
                  <span><strong>{s.students_count}</strong> <span className="text-slate-500 text-xs">estudiantes</span></span>
                  <span><strong>{s.users_count}</strong> <span className="text-slate-500 text-xs">usuarios</span></span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  {p ? (
                    <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
                      {p.name} · {fmt(p.price_per_student)}/est
                    </span>
                  ) : <span className="text-xs text-slate-400">Sin plan</span>}
                  <span className="text-sm font-semibold text-slate-800">{p ? fmt(p.price_per_student * (s.students_count || 0)) : "—"}</span>
                </div>
                <div className="mt-2"><ActivationBadge s={s} /></div>
                <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                  <Button size="sm" variant="outline" data-testid={`edit-school-${s.id}`} onClick={() => openEdit(s)}>Editar</Button>
                  <Button size="sm" variant="ghost" className={s.active ? "text-rose-600" : "text-emerald-600"} data-testid={`toggle-school-${s.id}`} onClick={() => toggle(s)}>
                    <Power className="w-4 h-4 mr-1" /> {s.active ? "Desactivar" : "Activar"}
                  </Button>
                </div>
              </div>
            );
          })}
          {schools.length === 0 && <p className="text-slate-400 text-sm col-span-full">Sin colegios registrados.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Editar colegio" : "Nuevo colegio"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Nombre</Label><Input data-testid="school-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nombre comercial</Label><Input data-testid="school-commercial-input" value={form.commercial_name} onChange={(e) => setForm({ ...form, commercial_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>RNC</Label><Input data-testid="school-rnc-input" value={form.rnc} onChange={(e) => setForm({ ...form, rnc: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Dirección</Label><Input data-testid="school-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Teléfonos (separados por coma)</Label><Input data-testid="school-phones-input" value={form.phones} onChange={(e) => setForm({ ...form, phones: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Correos (separados por coma)</Label><Input data-testid="school-emails-input" value={form.emails} onChange={(e) => setForm({ ...form, emails: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Plan de suscripción</Label>
              <Select value={form.plan_id || "_none"} onValueChange={(v) => setForm({ ...form, plan_id: v === "_none" ? "" : v })}>
                <SelectTrigger data-testid="school-plan-select"><SelectValue placeholder="Sin plan" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Sin plan</SelectItem>
                  {plans.filter((p) => p.active !== false).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} — {fmt(p.price_per_student)}/estudiante ({p.periodicity})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tiempo de activación</Label>
              <Select value={form.activation_preset} onValueChange={(v) => setForm({ ...form, activation_preset: v })}>
                <SelectTrigger data-testid="school-activation-preset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin límite</SelectItem>
                  <SelectItem value="7">7 días (prueba)</SelectItem>
                  <SelectItem value="15">15 días</SelectItem>
                  <SelectItem value="30">30 días (1 mes)</SelectItem>
                  <SelectItem value="90">90 días (3 meses)</SelectItem>
                  <SelectItem value="365">365 días (1 año)</SelectItem>
                  <SelectItem value="custom">Fecha personalizada…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.activation_preset === "custom" && (
              <div className="space-y-1.5">
                <Label>Activo hasta</Label>
                <Input type="date" data-testid="school-active-until" value={form.active_until} onChange={(e) => setForm({ ...form, active_until: e.target.value })} />
              </div>
            )}
            {editing?.active_until && (
              <p className="text-xs text-slate-400" data-testid="school-current-until">Actualmente: activo hasta {editing.active_until}</p>
            )}
            <div className="flex items-center gap-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
              <Switch data-testid="school-i18n-switch" checked={!!form.i18n_enabled} onCheckedChange={(v) => setForm({ ...form, i18n_enabled: v })} />
              <Label className="!mt-0">Habilitar traducción (Español / Inglés / Francés) para los usuarios de este colegio</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="cancel-school" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="submit-school" className="bg-blue-800 hover:bg-blue-700" onClick={submit}>{editing ? "Guardar" : "Crear"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
